import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Target, Loader2, CloudOff, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { EmptyState } from "@/components/common/EmptyState";
import { formatDate } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";

const INQ_STATUS = {
  new: "text-status-info",
  contacted: "text-status-warning",
  closed: "text-fundio-muted",
};

export default function Anfragen() {
  const { user } = useAuth();
  const scope = user?.role === "super_admin" ? "all" : "customers";
  const [state, setState] = useState(null);

  const load = useCallback(() => {
    setState(null);
    api.get("/organizations/summary/all-inquiries", { params: { scope } })
      .then((r) => setState(r.data))
      .catch(() => setState({ service_available: false, inquiries: [], reason: "FUNDIO Tenant Service nicht erreichbar." }));
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  if (state === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;

  const rows = state.inquiries || [];

  return (
    <div data-testid="anfragen-page">
      <PageHeader title="Anfragen / Leads" subtitle="Kundenanfragen (Leads) aus dem kanonischen Tenant-System (read-only). Bearbeitung erfolgt im FUNDIO-Kundenkonto.">
        <button data-testid="anfragen-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-fundio-border px-3 py-2 text-sm font-medium text-fundio-ink transition-colors hover:bg-fundio-subtle">
          <RefreshCw className="h-4 w-4" /> Aktualisieren
        </button>
      </PageHeader>

      <div data-testid="anfragen-readonly-note" className="mb-4 flex items-center gap-2 rounded-lg border border-fundio-border bg-fundio-subtle px-4 py-2 text-xs text-fundio-muted">
        <Target className="h-4 w-4" />
        <span><span className="font-semibold">READ ONLY</span> · Anfragen werden im FUNDIO-Kundenkonto bearbeitet, nicht über CONTROL.</span>
      </div>

      {!state.service_available ? (
        <div data-testid="anfragen-unavailable" className="card-surface flex flex-col items-center rounded-xl px-6 py-14 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-status-danger-bg"><CloudOff className="h-6 w-6 text-status-danger" /></span>
          <p className="mt-4 text-base font-semibold text-fundio-ink">FUNDIO Tenant Service nicht erreichbar</p>
          <p className="mt-1 text-sm text-fundio-muted">{state.reason}</p>
        </div>
      ) : (
        <Panel title={`Anfragen (${rows.length})`} testid="anfragen-panel">
          {!rows.length ? (
            <EmptyState icon={Target} title="Keine Anfragen" description="Es liegen derzeit keine Kundenanfragen vor." testid="anfragen-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-fundio-border text-left text-xs uppercase tracking-wide text-fundio-faint">
                    <th className="px-2 py-2 font-medium">Kunde</th>
                    <th className="px-2 py-2 font-medium">Objekt</th>
                    <th className="px-2 py-2 font-medium">Interessent</th>
                    <th className="px-2 py-2 font-medium">Nachricht</th>
                    <th className="px-2 py-2 font-medium">Status</th>
                    <th className="px-2 py-2 font-medium">Eingegangen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-fundio-border">
                  {rows.map((r, i) => {
                    const q = r.inquiry || {};
                    return (
                      <tr key={q.id || i} data-testid={`anfrage-row-${q.id || i}`} className="transition-colors hover:bg-fundio-subtle">
                        <td className="px-2 py-3 font-medium text-fundio-ink">
                          {r.company_name || "—"}
                          {r.is_demo && <span className="ml-2 inline-flex items-center rounded border border-status-warning-border bg-status-warning-bg px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wider text-status-warning">QA</span>}
                        </td>
                        <td className="px-2 py-3 text-fundio-muted">{q.item_title || "—"}</td>
                        <td className="px-2 py-3 text-fundio-muted">{q.name || "—"}{q.phone ? ` · ${q.phone}` : ""}</td>
                        <td className="max-w-xs truncate px-2 py-3 text-fundio-muted">{q.message || "—"}</td>
                        <td className={`px-2 py-3 text-xs font-medium ${INQ_STATUS[q.status] || "text-fundio-muted"}`}>{q.status || "—"}</td>
                        <td className="px-2 py-3 text-fundio-faint">{q.created_at ? formatDate(q.created_at) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
